import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { OfferLineTemplate } from "@/hooks/use-offers";
import type { OfferPhase } from "@/lib/offer-calc";
import { PHASE_OPTIONS, COST_TYPES, PRICING_TYPES, UNITS, type Draft } from "./constants";

interface OfferLineTemplateNewRowProps {
  draft: Draft;
  onChange: (patch: Partial<OfferLineTemplate>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}

export function OfferLineTemplateNewRow({
  draft,
  onChange,
  onSave,
  onCancel,
  saving,
}: OfferLineTemplateNewRowProps) {
  return (
    <tr className="border-b border-primary/30 bg-primary/5">
      <td className="py-1 px-2">
        <Input
          autoFocus
          value={draft.name ?? ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Navn (påkrevd)"
          className="h-7 text-xs"
        />
      </td>
      <td className="py-1 px-2">
        <Select value={draft.phase} onValueChange={(v) => onChange({ phase: v as OfferPhase })}>
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PHASE_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Select
          value={draft.cost_type}
          onValueChange={(v) => onChange({ cost_type: v })}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COST_TYPES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Select
          value={draft.pricing_type}
          onValueChange={(v) => onChange({ pricing_type: v as "hourly" | "fixed" })}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRICING_TYPES.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Select
          value={draft.default_unit ?? "t"}
          onValueChange={(v) => onChange({ default_unit: v })}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNITS.map((u) => (
              <SelectItem key={u} value={u}>
                {u}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Input
          type="number"
          value={String(draft.default_rate ?? "")}
          onChange={(e) =>
            onChange({ default_rate: e.target.value === "" ? null : Number(e.target.value) })
          }
          // VIB-1000: piltaster/mushjul skal ikke endre satsen.
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
          }}
          onWheel={(e) => e.currentTarget.blur()}
          className="h-7 text-xs text-right tabular-nums"
          placeholder="—"
        />
      </td>
      <td className="py-1 px-2">
        <Input
          type="number"
          step="0.25"
          value={String(draft.default_quantity ?? "")}
          onChange={(e) =>
            onChange({
              default_quantity: e.target.value === "" ? null : Number(e.target.value),
            })
          }
          className="h-7 text-xs text-right tabular-nums"
          placeholder="—"
        />
      </td>
      <td className="py-1 px-2">
        <Input
          value={draft.description ?? ""}
          onChange={(e) =>
            onChange({ description: e.target.value === "" ? null : e.target.value })
          }
          className="h-7 text-xs"
          placeholder="Valgfri beskrivelse"
        />
      </td>
      <td className="py-1 px-2 text-center">
        <input type="checkbox" checked disabled />
      </td>
      <td className="py-1 px-2 text-center">
        <input
          type="checkbox"
          aria-label="Medgått ny linje"
          checked={!!draft.is_medgatt}
          onChange={(e) => onChange({ is_medgatt: e.target.checked })}
        />
      </td>
      <td className="py-1 px-2 text-center">
        <input
          type="checkbox"
          checked={!!draft.is_default_for_offers}
          onChange={(e) => onChange({ is_default_for_offers: e.target.checked })}
        />
      </td>
      <td className="py-1 px-2">
        <Input
          type="number"
          value={String(draft.sort_order ?? 999)}
          onChange={(e) => onChange({ sort_order: Number(e.target.value) || 0 })}
          className="h-7 text-xs text-right tabular-nums"
        />
      </td>
      <td className="py-1 px-2">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" className="h-7 px-2 text-xs" onClick={onSave} disabled={saving}>
            Opprett
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={onCancel}
          >
            Avbryt
          </Button>
        </div>
      </td>
    </tr>
  );
}
